/**
 * /customers — Customer page.
 *
 * Honest "private beta" page during pre-launch. No invented logos, no fake
 * case studies, no fixture firms. Real principle: we are working with a
 * select cohort and will publish stories with permission.
 *
 * Replaces the Phase 9 ComingSoonShell stub per Phase 3 Task 3.7 of the
 * 2026-05-16 implementation plan.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { MarketingLayout } from '../components/layouts/marketing-layout';

export const Route = createFileRoute('/customers')({
  component: CustomersPage,
});

function CustomersPage() {
  useEffect(() => {
    document.title = 'Customers · Elreign: private beta with a select cohort of fiduciary firms';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'Elreign is in private beta with a select group of South African fiduciary firms. We will publish customer stories with explicit permission. Apply for early access today.',
    );
  }, []);

  return (
    <MarketingLayout>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '72px 24px 96px' }}>
        {/* Hero */}
        <header style={{ marginBottom: 56 }}>
          <p className="eyebrow" style={{ marginBottom: 18 }}>Early access</p>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(40px, 6vw, 64px)',
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              margin: '0 0 24px',
            }}
          >
            We are in <em>private beta.</em>
          </h1>
          <p
            style={{
              fontSize: 19,
              color: 'var(--ink-3)',
              lineHeight: 1.65,
              margin: 0,
              maxWidth: 620,
            }}
          >
            Elreign is currently in private beta with a select cohort of South African fiduciary
            firms. We onboard one firm at a time, in person, and we sit alongside the
            administrator for the first month.
          </p>
        </header>

        {/* Principles */}
        <section style={{ marginBottom: 56 }}>
          <h2
            className="serif"
            style={{
              fontSize: 26,
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              margin: '0 0 24px',
              letterSpacing: '-0.01em',
            }}
          >
            How we work with early customers
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <Principle
              number="01"
              heading="We do not publish customer names without permission"
              body="We will not list a firm on this page, in a press release, or in a sales conversation unless we have written permission. The fiduciary work our customers do is confidential by nature, and our customer list is treated the same way."
            />
            <Principle
              number="02"
              heading="We work with one new firm a month, in person"
              body="A new firm joins the platform with a full week of on-site implementation in the firm's office. We migrate the trust register together. We sit with the administrator for the first month of live use. This is how we keep the quality bar high."
            />
            <Principle
              number="03"
              heading="Pricing is open even in private beta"
              body="Our pricing is published. No hidden enterprise rates, no rug-pulls. The cost of running the platform is what it costs. Beta cohort pricing carries forward without surprise increases."
            />
            <Principle
              number="04"
              heading="Customer stories will be published with explicit consent"
              body="When a beta customer is ready to talk publicly about how they use Elreign, we will publish a story here, with their words, their numbers, and their attribution. Not before."
            />
          </div>
        </section>

        {/* CTA */}
        <section
          style={{
            padding: '40px 36px',
            background: 'var(--surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 'var(--r-xl)',
          }}
        >
          <h2
            className="serif"
            style={{
              fontSize: 28,
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              margin: '0 0 12px',
              letterSpacing: '-0.015em',
              lineHeight: 1.2,
            }}
          >
            Apply for early access
          </h2>
          <p style={{ fontSize: 16, color: 'var(--ink-3)', lineHeight: 1.6, marginTop: 0, marginBottom: 24, maxWidth: 540 }}>
            Tell us about your practice. We will respond within two business days with a sense of
            whether the fit is right and a proposed onboarding slot.
          </p>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Link
              to="/contact"
              style={{
                background: 'var(--accent-400)',
                color: 'var(--primary-900)',
                padding: '12px 24px',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Apply for early access
            </Link>
            <Link
              to="/pricing"
              style={{
                fontSize: 15,
                color: 'var(--primary-600)',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(31,77,69,0.25)',
                paddingBottom: 1,
                alignSelf: 'center',
              }}
            >
              See pricing
            </Link>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
}

interface PrincipleProps {
  number: string;
  heading: string;
  body: string;
}

function Principle({ number, heading, body }: PrincipleProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '40px 1fr',
        gap: 20,
        paddingTop: 20,
        borderTop: '1px solid var(--hairline)',
      }}
    >
      <div
        className="mono"
        style={{ fontSize: 13, color: 'var(--accent-600)', letterSpacing: '0.04em' }}
      >
        {number}
      </div>
      <div>
        <h3
          style={{
            fontSize: 17,
            fontWeight: 600,
            color: 'var(--ink)',
            margin: '0 0 8px',
            letterSpacing: '-0.01em',
          }}
        >
          {heading}
        </h3>
        <p style={{ fontSize: 16, color: 'var(--ink-2)', lineHeight: 1.65, margin: 0 }}>{body}</p>
      </div>
    </div>
  );
}
